import { useCallback, useEffect, useRef, RefObject } from 'react'

const EDGE_THRESHOLD = 40

const getScrollStep = (thresholdDelta: number) => {
  const delta = Math.max(1, Math.min(thresholdDelta, EDGE_THRESHOLD))
  return Math.round(EDGE_THRESHOLD / delta) * 2
}

const useScrollWhenDragging = ({
  scrollableContainerRef,
  isDisabled
}: {
  scrollableContainerRef: RefObject<HTMLElement | null>
  isDisabled: boolean
}) => {
  const scrollableContainer = scrollableContainerRef.current
  const scrollAnimationRef = useRef<number | null>(null)

  const hasLeftScroll = useCallback(() => {
    if (!scrollableContainer) return false
    return scrollableContainer.scrollLeft > 0
  }, [scrollableContainer])

  const hasRightScroll = useCallback(() => {
    if (!scrollableContainer) return false
    return (
      scrollableContainer.scrollLeft <
      scrollableContainer.scrollWidth - scrollableContainer.clientWidth
    )
  }, [scrollableContainer])

  const cancelScroll = useCallback(() => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current)
    }
  }, [])

  const scrollBy = useCallback(
    (value: number, e: MouseEvent | TouchEvent) => {
      if (!scrollableContainer) {
        return
      }

      const isTouchEvent = 'touches' in e

      const expectedScrollLeft = scrollableContainer.scrollLeft + value

      if (expectedScrollLeft < 0) {
        scrollableContainer.scrollLeft = 0
        cancelScroll()
        return
      } else if (
        expectedScrollLeft >
        scrollableContainer.scrollWidth - scrollableContainer.clientWidth
      ) {
        scrollableContainer.scrollLeft =
          scrollableContainer.scrollWidth - scrollableContainer.clientWidth
        cancelScroll()
        return
      }

      scrollableContainer.scrollLeft = expectedScrollLeft

      const customEvent = isTouchEvent
        ? new TouchEvent('touchmove', {
            touches: Array.from(e.touches),
            targetTouches: Array.from(e.targetTouches),
            changedTouches: Array.from(e.changedTouches),
            bubbles: true,
            cancelable: true
          })
        : new MouseEvent('mousemove', e)

      document.dispatchEvent(customEvent)
      scrollAnimationRef.current = requestAnimationFrame(() =>
        scrollBy(value, e)
      )
    },
    [scrollableContainer, cancelScroll]
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!scrollableContainer) return

      const { left: scrollableContainerLeft, right: scrollableContainerRight } =
        scrollableContainer.getBoundingClientRect()

      const mouseX = 'touches' in e ? e.touches[0].clientX : e.clientX

      cancelScroll()
      if (
        hasLeftScroll() &&
        mouseX - scrollableContainerLeft < EDGE_THRESHOLD
      ) {
        const scrollStep = getScrollStep(mouseX - scrollableContainerLeft)
        scrollBy(-scrollStep, e)
      } else if (
        hasRightScroll() &&
        scrollableContainerRight - mouseX < EDGE_THRESHOLD
      ) {
        const scrollStep = getScrollStep(scrollableContainerRight - mouseX)
        scrollBy(scrollStep, e)
      }
    },
    [scrollableContainer, hasLeftScroll, hasRightScroll, scrollBy, cancelScroll]
  )

  useEffect(() => {
    if (!isDisabled) {
      document.body.addEventListener('mousemove', handleMouseMove)
      document.body.addEventListener('touchmove', handleMouseMove)
    } else {
      cancelScroll()
      document.body.removeEventListener('mousemove', handleMouseMove)
      document.body.removeEventListener('touchmove', handleMouseMove)
    }
    return () => {
      cancelScroll()
      document.body.removeEventListener('mousemove', handleMouseMove)
      document.body.removeEventListener('touchmove', handleMouseMove)
    }
  }, [isDisabled, handleMouseMove, cancelScroll])
  return { cancelScroll }
}

export default useScrollWhenDragging
